import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { TabContext, TabPanel } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  Stack,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import {
  adminGetLessonHistoryById,
  getLessonHistoryState,
  resetAdminLessonHistoryData,
} from '../../../../store/V2/LessonHistorySlice';
import {
  adminAddNewStudentFeedbackData,
  adminDeleteStudentFeedbackData,
  adminGetLessonStudentFeedbacks,
  adminUpdateStudentFeedbackData,
  getLessonStudentFeedbacksState,
  ILessonStudentFeedback,
  resetAdminLessonStudentFeedbackListData,
} from '../../../../store/V2/LessonStudentFeedbacksSlice';
import {
  adminAddNewTutorFeedbackData,
  adminDeleteTutorFeedbackData,
  adminGetLessonTutorFeedbacks,
  adminUpdateTutorFeedbackData,
  getLessonTutorFeedbacksState,
  ILessonTutorFeedback,
  resetAdminLessonTutorFeedbackListData,
} from '../../../../store/V2/LessonTutorFeedbacksSlice';
import { IFeedbackStudent, IFeedbackTutor } from '../helper';
import { useAppSelector } from '../../../../hooks';
import { ISection } from '../../../../store/V2/SectionSlice';
import BasicLoader from '../../../Loaders/BasicLoader';
import TabBar, { ITabItem } from '../../Tabs/type1/TabBar';
import AddStudentFeedbackForm from './AddStudentFeedbackForm';
import GroupClassStudentFeedbackContents from './GroupClassStudentFeedbackContents';
import AddTutorFeedbackForm from './AddTutorFeedbackForm';
import GroupClassTutorFeedbackContents from './GroupClassTutorFeedbackContents';
import { IUser } from '../../../../store/UserSlice';
import { TPAutoCompleteArrowIconComponent, TPTextField } from '../../../Inputs';
import { getShortName } from '../../../../helper';

interface FeedbacksModalProps {
  open: boolean;
  onClose: () => void;
  lessonHistoryId: string;
}

type TState = {
  selectedTab: ITabItem;
  selectedTutor: IFeedbackTutor | null;
  selectedStudent: IFeedbackStudent | null;
};

const tabs: ITabItem[] = [
  {
    index: 0,
    key: 'studentFeedbacks',
    title: 'Student Feedback',
  },
  {
    index: 1,
    key: 'tutorFeedbacks',
    title: 'Tutor Feedback',
  },
];

const initialState: TState = {
  selectedTab: tabs[0],
  selectedTutor: null,
  selectedStudent: null,
};

const FeedbacksModal: FunctionComponent<FeedbacksModalProps> = ({
  open,
  onClose,
  lessonHistoryId,
}) => {
  const dispatch = useDispatch();

  // Store
  const lessonHistoryState = useAppSelector(getLessonHistoryState);
  const lessonStudentFeedbacksState = useAppSelector(
    getLessonStudentFeedbacksState
  );

  const lessonTutorFeedbacksState = useAppSelector(
    getLessonTutorFeedbacksState
  );

  // State
  const [state, setState] = useState<TState>({ ...initialState });
  const [initialLoading, setInitialLoading] = useState(true);

  const lessonHistory = lessonHistoryState.adminLessonHistoryData;
  const sectionData = lessonHistory
    ? (lessonHistory.section as ISection)
    : undefined;

  const studentList =
    lessonHistory && lessonHistory.students && lessonHistory.students.length
      ? lessonHistory.students
      : [];

  const tutorList =
    lessonHistory && lessonHistory.tutors && lessonHistory.tutors.length
      ? lessonHistory.tutors
      : [];

  const studentFeedbacks =
    lessonStudentFeedbacksState.adminLessonStudentFeedbackListData &&
    lessonStudentFeedbacksState.adminLessonStudentFeedbackListData.list &&
    lessonStudentFeedbacksState.adminLessonStudentFeedbackListData.list.length
      ? lessonStudentFeedbacksState.adminLessonStudentFeedbackListData.list
      : [];

  const tutorFeedbacks =
    lessonTutorFeedbacksState.adminLessonTutorFeedbackListData &&
    lessonTutorFeedbacksState.adminLessonTutorFeedbackListData.list &&
    lessonTutorFeedbacksState.adminLessonTutorFeedbackListData.list.length
      ? lessonTutorFeedbacksState.adminLessonTutorFeedbackListData.list
      : [];

  const feedbackStudents: IFeedbackStudent[] = studentList.map((item) => ({
    _id: item._id,
    firstname: item.firstname,
    lastname: item.lastname,
  }));

  const feedbackTutors: IFeedbackTutor[] = tutorList.map((item) => ({
    _id: item._id,
    firstname: item.firstname,
    lastname: item.lastname,
  }));

  useEffect(() => {
    dispatch(adminGetLessonHistoryById({ id: lessonHistoryId }));

    return () => {
      dispatch(resetAdminLessonHistoryData());
      dispatch(resetAdminLessonStudentFeedbackListData());
      dispatch(resetAdminLessonTutorFeedbackListData());
    };
  }, []);

  useEffect(() => {
    if (lessonHistory) {
      initializeFeedbackData();
    }
  }, [lessonHistory]);

  const initializeFeedbackData = async () => {
    if (sectionData) {
      await dispatch(
        adminGetLessonStudentFeedbacks({
          limit: 200,
          offset: 0,
          section: sectionData._id,
        })
      );

      await dispatch(
        adminGetLessonTutorFeedbacks({
          limit: 200,
          offset: 0,
          section: sectionData._id,
        })
      );
    }

    setInitialLoading(false);
  };

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const onStudentFeedbackAdded = (feedbackData: ILessonStudentFeedback[]) => {
    dispatch(adminAddNewStudentFeedbackData(feedbackData));
  };

  const onStudentFeedbackDeleted = (id: string) => {
    dispatch(adminDeleteStudentFeedbackData({ id }));
  };

  const onStudentFeedbackUpdated = (
    id: string,
    feedbackData: ILessonStudentFeedback
  ) => {
    dispatch(
      adminUpdateStudentFeedbackData({
        id,
        feedback: feedbackData,
      })
    );
  };

  const onTutorFeedbackAdded = (feedbackData: ILessonTutorFeedback[]) => {
    dispatch(adminAddNewTutorFeedbackData(feedbackData));
  };

  const onTutorFeedbackUpdated = (
    id: string,
    feedbackData: ILessonTutorFeedback
  ) => {
    dispatch(
      adminUpdateTutorFeedbackData({
        id,
        feedback: feedbackData,
      })
    );
  };

  const onTutorFeedbackDeleted = (id: string) => {
    dispatch(adminDeleteTutorFeedbackData({ id }));
  };

  const renderStudentFeedbackContents = () => {
    if (sectionData && feedbackStudents.length) {
      if (feedbackStudents.length === 1) {
        const _student = feedbackStudents[0];

        if (feedbackTutors.length > 1) {
          let matchingFeedback: ILessonStudentFeedback | undefined;

          if (state.selectedTutor) {
            matchingFeedback = studentFeedbacks.find((item) => {
              const _feedbackTutor = item.tutor as IUser;
              const _feedbackStudent = item.student as IUser;

              return (
                _feedbackTutor._id === state.selectedTutor?._id &&
                _feedbackStudent._id === _student._id
              );
            });
          }

          return (
            <Fade in>
              <div style={{ padding: '16px 0' }}>
                <Stack mb="10px">
                  <Autocomplete
                    fullWidth
                    options={feedbackTutors}
                    filterSelectedOptions
                    value={state.selectedTutor}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    isOptionEqualToValue={(option, value) =>
                      option._id === value._id
                    }
                    onChange={(e, value) =>
                      updateStateAttributes({ selectedTutor: value })
                    }
                    getOptionLabel={(option) =>
                      getShortName(option.firstname, option.lastname, true)
                    }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        InputLabelProps={{ shrink: true }}
                        label="Tutor *"
                        placeholder="Select"
                      />
                    )}
                  />
                </Stack>
                {state.selectedTutor ? (
                  <AddStudentFeedbackForm
                    sectionId={sectionData._id}
                    feedback={matchingFeedback}
                    onFeedbackAdded={onStudentFeedbackAdded}
                    onFeedbackUpdated={onStudentFeedbackUpdated}
                    students={[_student]}
                    tutor={state.selectedTutor}
                  />
                ) : null}
              </div>
            </Fade>
          );
        }

        const feedbackTutor = feedbackTutors[0];
        const studentFeedback: ILessonStudentFeedback | undefined =
          studentFeedbacks[0];

        return (
          <Fade in>
            <div style={{ padding: '16px 0' }}>
              <AddStudentFeedbackForm
                sectionId={sectionData._id}
                feedback={studentFeedback}
                onFeedbackAdded={onStudentFeedbackAdded}
                onFeedbackUpdated={onStudentFeedbackUpdated}
                students={feedbackStudents}
                tutor={feedbackTutor}
              />
            </div>
          </Fade>
        );
      }

      return (
        <Fade in>
          <div style={{ padding: '16px 0' }}>
            <GroupClassStudentFeedbackContents
              sectionId={sectionData._id}
              feedbacks={studentFeedbacks}
              students={feedbackStudents}
              tutors={feedbackTutors}
              onFeedbackAdded={onStudentFeedbackAdded}
              onFeedbackUpdated={onStudentFeedbackUpdated}
              onFeedbackDeleted={onStudentFeedbackDeleted}
            />
          </div>
        </Fade>
      );
    }

    return null;
  };

  const renderTutorFeedbackContents = () => {
    if (sectionData && feedbackTutors.length) {
      if (feedbackTutors.length === 1) {
        const _tutor = feedbackTutors[0];

        if (feedbackStudents.length > 1) {
          let matchingFeedback: ILessonTutorFeedback | undefined;

          if (state.selectedStudent) {
            matchingFeedback = tutorFeedbacks.find((item) => {
              const _feedbackTutor = item.tutor as IUser;
              const _feedbackStudent = item.student as IUser;

              return (
                _feedbackStudent._id === state.selectedStudent?._id &&
                _feedbackTutor._id === _tutor._id
              );
            });
          }

          return (
            <Fade in>
              <div style={{ padding: '16px 0' }}>
                <Stack mb="10px">
                  <Autocomplete
                    fullWidth
                    options={feedbackStudents}
                    filterSelectedOptions
                    value={state.selectedStudent}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    isOptionEqualToValue={(option, value) =>
                      option._id === value._id
                    }
                    onChange={(e, value) =>
                      updateStateAttributes({ selectedStudent: value })
                    }
                    getOptionLabel={(option) =>
                      getShortName(option.firstname, option.lastname, true)
                    }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        InputLabelProps={{ shrink: true }}
                        label="Student *"
                        placeholder="Select"
                      />
                    )}
                  />
                </Stack>
                {state.selectedStudent ? (
                  <AddTutorFeedbackForm
                    sectionId={sectionData._id}
                    feedback={matchingFeedback}
                    onFeedbackAdded={onTutorFeedbackAdded}
                    onFeedbackUpdated={onTutorFeedbackUpdated}
                    student={state.selectedStudent}
                    tutors={[_tutor]}
                  />
                ) : null}
              </div>
            </Fade>
          );
        }

        const feedbackStudent = feedbackStudents[0];
        const tutorFeedback: ILessonTutorFeedback | undefined =
          tutorFeedbacks[0];

        return (
          <Fade in>
            <div style={{ padding: '16px 0' }}>
              <AddTutorFeedbackForm
                sectionId={sectionData._id}
                feedback={tutorFeedback}
                onFeedbackAdded={onTutorFeedbackAdded}
                onFeedbackUpdated={onTutorFeedbackUpdated}
                student={feedbackStudent}
                tutors={feedbackTutors}
              />
            </div>
          </Fade>
        );
      }

      return (
        <Fade in>
          <div style={{ padding: '16px 0' }}>
            <GroupClassTutorFeedbackContents
              sectionId={sectionData._id}
              feedbacks={tutorFeedbacks}
              tutors={feedbackTutors}
              students={feedbackStudents}
              onFeedbackAdded={onTutorFeedbackAdded}
              onFeedbackUpdated={onTutorFeedbackUpdated}
              onFeedbackDeleted={onTutorFeedbackDeleted}
            />
          </div>
        </Fade>
      );
    }

    return null;
  };

  const renderTabPanel = () => {
    let tabPanel: JSX.Element | null = null;

    const panelProps = {
      value: state.selectedTab.index.toString(),
      sx: { p: 0 },
    };

    switch (state.selectedTab.index) {
      case 0:
        tabPanel = (
          <TabPanel {...panelProps}>{renderStudentFeedbackContents()}</TabPanel>
        );
        break;

      case 1:
        tabPanel = (
          <TabPanel {...panelProps}>{renderTutorFeedbackContents()}</TabPanel>
        );
        break;

      default:
        tabPanel = null;
        break;
    }

    return tabPanel;
  };

  const renderContents = () => {
    if (lessonHistory && sectionData) {
      return (
        <div>
          <TabContext value={state.selectedTab.index.toString()}>
            <div>
              <TabBar
                selectedTab={state.selectedTab}
                tabs={tabs}
                tabsProps={{ variant: 'fullWidth' }}
                onChange={(e, value) =>
                  updateStateAttributes({ selectedTab: tabs[value] })
                }
              />
            </div>
            <div>{renderTabPanel()}</div>
          </TabContext>
        </div>
      );
    }

    return null;
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px', maxWidth: '520px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        User Feedbacks
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent sx={{ p: { xs: '0 15px', sm: '0 30px' } }}>
        <Box sx={{ pb: { xs: '15px', sm: '30px' } }}>
          {initialLoading ? (
            <div style={{ padding: '16px 40px' }}>
              <BasicLoader />
            </div>
          ) : (
            renderContents()
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbacksModal;
